/**
 * FactoryBomPage — dedicated page cho `/factory/<code>/data/bom`.
 *
 * Bám Access spec `frmFactoryMDQryBOM` (list/query form) + `frmFactoryMDQryBOMAdd`
 * (Add/Edit single-form). List columns lấy từ view `public.v_factory_bom`
 * (bom_code, bom_type, processing_loss, moisture_loss, note, quality_input,
 * quality_output, date_update, person_update — chỉ render những cột Access
 * caption).
 *
 * Format y chang `/sales/contract/list` — TableVirtuoso + app-dataframe toolbar
 * + filter popovers + columns menu + sortable headers + BrandState.
 */
import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TextField,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { useEffect, useMemo, useRef, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'

import BrandState from '@/components/BrandState'
import ConfirmDialog from '@/components/ConfirmDialog'
import ResizeHandle from '@/components/ResizeHandle'
import {
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterBar,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '@/components/dataList'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateFactoryBomMutation,
  useDeleteFactoryBomMutation,
  useGetFactoryBomDetailQuery,
  useGetFactoryBomListQuery,
  useGetFactoryBomOptionsQuery,
  useUpdateFactoryBomMutation,
  type BomListRow,
  type BomSavePayload,
} from '@/store/api/factoryBomApi'
import { FuturesColumnsMenu } from '@/pages/futures/FuturesColumnsMenu'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'
import {
  FILTER_HEADER_SELECT_SX,
  HIDE_ON_MOBILE_INLINE,
  HIDE_ON_TABLET_INLINE,
  TABLE_PAGINATION_COMPACT,
} from '@/theme/responsive'

import BomFormDialog from './BomFormDialog'

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

type ColumnKind = 'text' | 'date' | 'percent'
type ColumnDef = {
  key: string
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
}

const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'crop_year', label: 'Crop Year' },
  { key: 'bom_code', label: 'BOM Code' },
  { key: 'bom_type', label: 'BOM Type' },
  { key: 'quality_input', label: 'Quality Input' },
  { key: 'quality_output', label: 'Quality Output' },
  { key: 'processing_loss', label: 'Processing Loss', kind: 'percent', align: 'right' },
  { key: 'moisture_loss', label: 'Moisture Loss', kind: 'percent', align: 'right' },
  { key: 'note', label: 'Note' },
  { key: 'updated_date', label: 'Updated Date', kind: 'date' },
  { key: 'updated_by', label: 'Updated Person' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (value == null || value === '') return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  return `${day}-${month}-${d.getFullYear()}`
}

const formatAccessPercent = (value: unknown): string => {
  if (value == null || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return ''
  return `${(n * 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
}

const distinctValues = (rows: BomListRow[], key: string): string[] => {
  const values = new Set<string>()
  for (const row of rows) {
    const value = row[key]
    if (value == null) continue
    const text = String(value).trim()
    if (text) values.add(text)
  }
  return [...values].sort((a, b) => a.localeCompare(b))
}

const cellValue = (row: BomListRow, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null || val === '') return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'percent') return formatAccessPercent(val)
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  return String(val)
}

interface FactoryBomPageProps {
  factoryCode: string
  /** Back-to-hub callback. Khi user trở về `/factory/<code>/data`. */
  onBackToHub: () => void
  canEdit: boolean
}

/** Composite selector — `(bom_code, crop_year)` vì cùng bom_code có thể tồn
 *  tại ở nhiều crop_year. Encode dạng `bom_code||crop_year` cho selectedKey. */
type DialogState =
  | { kind: 'closed' }
  | { kind: 'new' }
  | { kind: 'edit'; bomCode: string; cropYear: string }

interface PendingDelete {
  bomCode: string
  cropYear: string
}

const rowKey = (row: { bom_code: string; crop_year: string | null }): string =>
  `${row.bom_code}||${row.crop_year ?? ''}`

const ALL_ROWS_PAGE_SIZE = -1

export default function FactoryBomPage({
  factoryCode,
  onBackToHub,
  canEdit,
}: FactoryBomPageProps) {
  const snack = useSnackbar()

  const [cropYearFilter, setCropYearFilter] = useState('')
  const cropYearDefaultedRef = useRef(false)

  const list = useGetFactoryBomListQuery({ factoryCode })
  const options = useGetFactoryBomOptionsQuery(factoryCode)

  // Default crop year filter = crop chứa ngày hôm nay (tra d_date.crops).
  // Chỉ set một lần khi options về lần đầu — sau đó user tự đổi (kể cả về "All").
  useEffect(() => {
    if (cropYearDefaultedRef.current) return
    if (!options.data) return
    cropYearDefaultedRef.current = true
    const current = options.data.currentCropYear
    if (current) setCropYearFilter(current)
  }, [options.data])

  const {
    selectedId: selectedKey,
    setSelectedId: setSelectedKey,
    toggleSelect: toggleSelectKey,
  } = useRowSelection<string>()
  const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' })
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null)

  const [createBom, { isLoading: creating }] = useCreateFactoryBomMutation()
  const [updateBom, { isLoading: updating }] = useUpdateFactoryBomMutation()
  const [deleteBom, { isLoading: deleting }] = useDeleteFactoryBomMutation()

  const editDetail = useGetFactoryBomDetailQuery(
    dialog.kind === 'edit'
      ? { factoryCode, bomCode: dialog.bomCode, cropYear: dialog.cropYear }
      : { factoryCode, bomCode: '', cropYear: '' },
    { skip: dialog.kind !== 'edit' },
  )

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(ALL_ROWS_PAGE_SIZE)

  const { getWidth, startResize } = useColumnWidths('factory.bom.list')

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)

  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const columnsCtrl = usePreferenceColumns<ColumnDef>({
    scope: 'factory',
    prefKey: 'columns',
    subKey: 'bom',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'factory.bom.list',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const baseRows = useMemo(() => list.data?.items ?? [], [list.data])

  const filteredRows = useMemo(
    () =>
      baseRows.filter((row) => {
        if (!rowMatchesSearch(row, searchTerm, TABLE_COLUMNS)) return false
        if (cropYearFilter && row.crop_year !== cropYearFilter) return false
        if (!rowMatchesAllColumnFilters(row, columnFilters.filters)) return false
        return true
      }),
    [baseRows, searchTerm, cropYearFilter, columnFilters.filters],
  )

  const hasActiveFilters =
    !!searchTerm.trim() || !!cropYearFilter || columnFilters.hasAny
  const activeFilterCount =
    (searchTerm.trim() ? 1 : 0) + (cropYearFilter ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    setCropYearFilter('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
  }

  const popoverColumn = useMemo(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, colPopover.key)
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const sortedRows = useMemo(
    () => sortTableRows(filteredRows, sortState, TABLE_COLUMNS),
    [filteredRows, sortState],
  )

  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])

  const cropYearOptions = useMemo(() => {
    const fromOptions = options.data?.cropYears ?? []
    if (fromOptions.length) return fromOptions
    return distinctValues(baseRows, 'crop_year')
  }, [options.data?.cropYears, baseRows])

  const minTableWidth = Math.max(960, visibleColumns.length * 120)
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  const toggleSort = (key: string) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const selectedRow = useMemo(
    () => baseRows.find((r) => rowKey(r) === selectedKey) ?? null,
    [baseRows, selectedKey],
  )

  const handleSave = async (payload: BomSavePayload) => {
    if (!canEdit) {
      snack.error('Read-only — you do not have edit permission for this factory.')
      return
    }
    try {
      if (dialog.kind === 'new') {
        await createBom({ factoryCode, body: payload }).unwrap()
        snack.success('BOM created')
      } else if (dialog.kind === 'edit') {
        await updateBom({
          factoryCode,
          bomCode: dialog.bomCode,
          cropYear: dialog.cropYear,
          body: payload,
        }).unwrap()
        snack.success('BOM updated')
      }
      setDialog({ kind: 'closed' })
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { detail?: string } }).data?.detail ?? 'Save failed')
          : 'Save failed'
      snack.error(msg)
    }
  }

  const handleConfirmDelete = async () => {
    if (!canEdit) {
      snack.error('Read-only — you do not have edit permission for this factory.')
      return
    }
    if (!pendingDelete) return
    try {
      await deleteBom({
        factoryCode,
        bomCode: pendingDelete.bomCode,
        cropYear: pendingDelete.cropYear,
      }).unwrap()
      snack.success('BOM deleted')
      const deletedKey = `${pendingDelete.bomCode}||${pendingDelete.cropYear}`
      if (selectedKey === deletedKey) setSelectedKey(null)
      setPendingDelete(null)
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { detail?: string } }).data?.detail ?? 'Delete failed')
          : 'Delete failed'
      snack.error(msg)
    }
  }

  const handleEditClick = () => {
    if (!canEdit) return
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    setDialog({
      kind: 'edit',
      bomCode: selectedRow.bom_code,
      cropYear: selectedRow.crop_year ?? '',
    })
  }

  const handleDeleteClick = () => {
    if (!canEdit) return
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    setPendingDelete({
      bomCode: selectedRow.bom_code,
      cropYear: selectedRow.crop_year ?? '',
    })
  }

  const saving = creating || updating

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Action + filter bar */}
      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 0.5 }}
      >
        <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center" sx={{ minWidth: 0 }}>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
            onClick={onBackToHub}
            sx={filterBarBtnSx}
          >
            Datasets
          </Button>
          {/* Page title — solid accent pill (icon + label trên cùng background
              accent color, text trắng) để user vào page là nhận ra context ngay. */}
          <Stack
            direction="row"
            spacing={0.625}
            alignItems="center"
            sx={{
              mx: 0.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: '#4a3d8f',
              color: '#fff',
              boxShadow: `0 1px 3px ${alpha('#4a3d8f', 0.45)}`,
            }}
          >
            <AccountTreeIcon sx={{ fontSize: '0.95rem', color: '#fff' }} />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                fontSize: '0.85rem',
                color: '#fff',
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                whiteSpace: 'nowrap',
                lineHeight: 1.1,
              }}
            >
              BOM
            </Typography>
          </Stack>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25, fontWeight: 400 }}>
            Filter
          </Typography>
          <FilterButton
            label="Search"
            active={!!searchTerm.trim()}
            onClick={(e) => filterPopover.open('search', e)}
          />
          {hasActiveFilters && (
            <Button size="small" variant="text" color="secondary" onClick={clearAllFilters} sx={filterBarBtnSx}>
              Clear All{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openColMenu(e.currentTarget)}
            sx={filterBarBtnSx}
            title="Columns"
          >
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
            <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
          </Button>
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
          <TextField
            label="Crop Year"
            select
            size="small"
            value={cropYearFilter}
            onChange={(e) => { setCropYearFilter(String(e.target.value)); setPage(0) }}
            InputLabelProps={{ shrink: true }}
            sx={FILTER_HEADER_SELECT_SX}
          >
            <MenuItem value="" sx={{ fontSize: '0.78rem' }}>
              <em>All</em>
            </MenuItem>
            {cropYearOptions.map((value) => (
              <MenuItem key={value} value={value} sx={{ fontSize: '0.78rem' }}>
                {value}
              </MenuItem>
            ))}
          </TextField>
          <Tooltip title={canEdit ? 'New' : 'Read-only — contact admin'}>
            <span>
              <Button
                variant="contained"
                size="small"
                color="success"
                sx={actionToolbarBtnSx}
                disabled={!canEdit || saving}
                onClick={() => setDialog({ kind: 'new' })}
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                title="New"
              >
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            sx={actionToolbarBtnSx}
            disabled={!canEdit || !selectedRow || saving}
            onClick={handleEditClick}
            startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
            title="Edit"
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="error"
            sx={actionToolbarBtnSx}
            disabled={!canEdit || !selectedRow || deleting}
            onClick={handleDeleteClick}
            startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
            title="Delete"
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
          </Button>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 0.5 }} />

      {list.isError ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load BOM list.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        {/* Toolbar — pagination + counts */}
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            flexWrap: 'wrap',
            minHeight: 24,
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(list.isLoading || list.isFetching) && <CircularProgress size={15} />}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}
            >
              Total: {filteredRows.length}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}
            >
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={filterPopover.close}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Code, type, quality..."
              label="Search BOM"
              onClear={filterPopover.close}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(colPopover.key, v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        {/* Virtualized table */}
        <Box sx={{ flex: 1, minHeight: { xs: 280, sm: 320 }, overflow: 'hidden' }}>
          {list.isError ? (
            <BrandState
              tone="error"
              title="Could not load BOM list"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : list.isLoading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No BOM records found"
              description="Try adjusting the search or BOM Type filter above, or create a new BOM."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const w = getWidth(col.key)
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', position: 'relative' }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(col.key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(col.key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: BomListRow) =>
                visibleColumns.map((col) => {
                  const val = cellValue(row, col)
                  const w = getWidth(col.key)
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      <Box
                        component="span"
                        className="app-table-cell-text"
                        title={val || '-'}
                        sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => (
                  <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />
                ),
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const row = Number.isFinite(idx) ? rows[idx] : null
                  const key = row ? rowKey(row) : null
                  const isSelected = key != null && key === selectedKey
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={isSelected}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (key != null) toggleSelectKey(key)
                      }}
                      onDoubleClick={() => {
                        if (!canEdit || row == null) return
                        setDialog({
                          kind: 'edit',
                          bomCode: row.bom_code,
                          cropYear: row.crop_year ?? '',
                        })
                      }}
                    />
                  )
                },
              }}
            />
          )}
        </Box>
      </Box>

      <BomFormDialog
        open={dialog.kind !== 'closed'}
        mode={dialog.kind === 'new' ? 'new' : 'edit'}
        detail={dialog.kind === 'edit' ? editDetail.data ?? null : null}
        detailLoading={dialog.kind === 'edit' && editDetail.isFetching}
        saving={saving}
        options={options.data ?? null}
        optionsLoading={options.isFetching}
        defaultCropYear={cropYearFilter}
        canEdit={canEdit}
        onClose={() => setDialog({ kind: 'closed' })}
        onSave={handleSave}
      />

      <ConfirmDialog
        open={pendingDelete != null}
        title="Delete BOM"
        message={
          pendingDelete
            ? `Delete BOM "${pendingDelete.bomCode}" (crop year ${pendingDelete.cropYear})? `
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        danger
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <FuturesColumnsMenu controller={columnsCtrl} />
    </Box>
  )
}
